import React, {useCallback, useEffect, useState} from "react";
import {Grid, MenuItem, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {FormProvider, useForm} from "react-hook-form";
import {Application, applicationSchema} from "./Application";
import {makeRequired} from "../../shared/components/forms/formUtils";
import {handleFormError, RhfForm} from "../../shared/components/forms/RhfForm";
import {FormHeader} from "../../shared/components/forms/FormHeader";
import {MuiRhfTextField} from "../../shared/components/forms/MuiRhfTextField";
import {yupResolver} from "@hookform/resolvers/yup";
import Typography from "@mui/material/Typography";
import {useAxios} from "../../shared/technical/rest/AxiosProvider";
import {syncApplicationUsers} from "./ApplicationsApi";
import {useAccount} from "../users/AccountProvider";
import {mayEditApplication} from "./permissions";
import {MuiRhfStringListDialogField} from "../../shared/components/forms/MuiRhfStringListDialogField";
import {ApplicationSubPageContentContainer} from "./ApplicationSubPageContentContainer";

export function ApplicationInformationSubPage(props: {
  application: Application,
  handleSave: (application: Application) => Promise<Application>,
  handleDelete: (application: Application) => void,
}) {
  const {application, handleSave, handleDelete} = props;
  const {t} = useTranslation();
  const theme = useTheme();
  const axios = useAxios();
  const account = useAccount();
  const [formReadonly, setFormReadonly] = useState<boolean>(!!props.application.id);
  const methods = useForm<Application>({
    mode: 'onBlur',
    defaultValues: application,
    resolver: yupResolver(applicationSchema(t))
  });
  const {register, reset, setError} = methods;

  useEffect(() => {
    register('id');
  }, [register]);

  useEffect(() => {
    reset(application);
  }, [application, reset]);

  const handleSyncUsers = useCallback(() => {
    syncApplicationUsers(axios, application.id!!).then()
  }, [axios, application]);

  const saveApplication = useCallback((application: Application) => {
    handleSave(application).then((result) => setFormReadonly(true), (error) => {
      handleFormError(error, setError, t);
    });
  }, [handleSave, setError, t])

  const stdFieldProps = {
    variant: "outlined" as "outlined",
    disabled: formReadonly,
  }

  const inputFieldProps = {
    ...stdFieldProps,
    InputLabelProps: {
      shrink: true
    }
  }
  const required = makeRequired(applicationSchema(t));
  return (
    <ApplicationSubPageContentContainer>
      <FormProvider {...methods} >
        <RhfForm id={'application-form'} onSubmit={saveApplication}>
          <FormHeader title={t('ApplicationPage.subPageInformation')}
                      onCancel={() => {
                        reset(application);
                        setFormReadonly(true);
                      }}
                      onDelete={() => handleDelete(application)}
                      canBeEdited={mayEditApplication(account, application)}
                      readonly={formReadonly}
                      onStartEditing={() => setFormReadonly(false)}
                      additionalMenuEntry={<MenuItem onClick={handleSyncUsers}>{t('Application.menuSyncUsers')}</MenuItem>}
                      hideDialogControls={true}
          />
          <Grid container={true} sx={{padding: theme.spacing(1), width: '100%'}}>
            <Grid item={true} xs={12} lg={12} sx={{marginTop: 1}}>
              <Grid container={true} spacing={3}>
                <Grid item={true} xs={12} lg={6}>
                  <MuiRhfTextField name={'name'}
                                   label={t('Application.name')}
                                   fullWidth={true}
                                   required={required?.name}
                                   helperText={t('Application.nameHelperText')}
                                   {...inputFieldProps}
                  />
                </Grid>
                <Grid item={true} xs={12} lg={6}>
                  <MuiRhfTextField name={'url'}
                                   label={t('Application.url')}
                                   fullWidth={true}
                                   required={required?.url}
                                   helperText={t('Application.urlHelperText')}
                                   {...inputFieldProps} />
                </Grid>
                <Grid item={true} xs={12} lg={6}>
                  <MuiRhfTextField name={'keycloakClientId'}
                                   label={t('Application.keycloakClientId')}
                                   fullWidth={true}
                                   required={required?.keycloakClientId}
                                   helperText={t('Application.keycloakClientIdHelperText')}
                                   {...inputFieldProps} />
                </Grid>
                <Grid item={true} xs={12} lg={6} sx={{alignItems: 'center', display: 'flex', justifyContent: 'center',}}>
                  <MuiRhfStringListDialogField name={'validRedirectUris'} title={t('Application.validRedirectUris')}
                                               inputLabel={t('Application.labelEnterNewUriHere')} disabled={formReadonly} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {!formReadonly &&
            <Typography variant={"caption"}>{t('Application.updateMessage')}</Typography>}
        </RhfForm>
      </FormProvider>
    </ApplicationSubPageContentContainer>
  )
}
